import { useState } from "react";
import { Alert, Box, Button, Card, CardContent, FormControlLabel, Grid, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material";
import rsiMomentumService from "@/services/rsiMomentumService";
import type { RsiMomentumRules } from "@/types";
import { extractErrorMessage } from "@/utils/errors";
import { PROPOSED_RULES, readRules, rulesErrors, type NumKey, type RulesForm as Form } from "./rsiMomentumRules";

/**
 * "Trade rules" -- the optional entry filter and trade management (pure ATR
 * stop, first target with partial booking, breakeven, ATR trail). Save
 * creates a new config version; "Backtest these rules" runs a what-if
 * backtest with the form values without saving anything.
 */
export default function RsiMomentumRulesCard({
  effective,
  onSaved,
  onWhatIf,
  whatIfReady,
}: {
  effective: Record<string, unknown> | null;
  onSaved: () => Promise<void>;
  onWhatIf: (rules: RsiMomentumRules) => void;
  whatIfReady: boolean;
}) {
  const [f, setF] = useState<Form>(() => readRules(effective));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const problems = rulesErrors(f);

  const setNum = (k: NumKey) => (e: { target: { value: string } }) => setF((v) => ({ ...v, [k]: Number(e.target.value) }));
  const toggle = (k: keyof Form, label: string) => (
    <FormControlLabel
      control={<Switch checked={Boolean(f[k])} onChange={(e) => setF((v) => ({ ...v, [k]: e.target.checked }))} inputProps={{ "aria-label": label }} />}
      label={label}
    />
  );
  const num = (k: NumKey, label: string, help: string, disabled = false, step = 0.05) => (
    <TextField
      fullWidth size="small" type="number" label={label} value={f[k]} onChange={setNum(k)} helperText={help} disabled={disabled}
      inputProps={{ step, "aria-label": label }}
    />
  );

  const save = async () => {
    setSaving(true);
    setErr(null);
    setOk(null);
    try {
      const res = await rsiMomentumService.setRules(f);
      setOk(`Saved as config v${res.version}.`);
      await onSaved();
    } catch (e) {
      setErr(extractErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }} data-testid="rules-card">
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Trade rules
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The signal (RSI, EMA/WMA cross, VWAP, EMA20) is unchanged. These options change the entry filter and how a trade is managed.
          Saving creates a new config version used by paper (and, if approved, live) trading; “Backtest these rules” changes nothing.
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField select fullWidth size="small" label="Stop mode" value={f.stop_mode}
              onChange={(e) => setF((v) => ({ ...v, stop_mode: e.target.value as Form["stop_mode"] }))}
              helperText={f.stop_mode === "ATR" ? "Entry ∓ ATR × multiple" : "Tighter of signal-bar low/high and ATR stop"}>
              <MenuItem value="SIGNAL_BAR_OR_ATR">Signal bar or ATR (original)</MenuItem>
              <MenuItem value="ATR">Pure ATR</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>{num("atr_stop_multiplier", "ATR stop multiple", "× ATR(14)")}</Grid>
          <Grid item xs={12} sm={6} md={3}>{num("reward_risk_ratio", "Final target (R)", "× initial risk")}</Grid>
          <Grid item xs={12} sm={6} md={3}>{num("max_trades_per_day", "Max trades per day", "New entries per session", false, 1)}</Grid>

          <Grid item xs={12} md={4}>
            {toggle("candle_quality_enabled", "Candle quality filter")}
            {num("close_position_pct", "Close in top/bottom %", "Long: close in top X% of range", !f.candle_quality_enabled, 5)}
          </Grid>
          <Grid item xs={12} md={4}>
            {toggle("first_target_enabled", "First target + partial booking")}
            <Stack direction="row" spacing={1}>
              {num("first_target_r", "First target (R)", "× initial risk", !f.first_target_enabled)}
              <TextField fullWidth size="small" type="number" label="Book %" value={Math.round(f.book_fraction * 100)}
                onChange={(e) => setF((v) => ({ ...v, book_fraction: Number(e.target.value) / 100 }))}
                helperText="Whole lots only" disabled={!f.first_target_enabled} inputProps={{ step: 5, "aria-label": "Book %" }} />
            </Stack>
            <Box sx={{ opacity: f.first_target_enabled ? 1 : 0.5 }}>{toggle("move_stop_to_breakeven", "Move stop to breakeven at first target")}</Box>
          </Grid>
          <Grid item xs={12} md={4}>
            {toggle("trailing_enabled", "ATR trailing stop (after first target)")}
            {num("trailing_atr_multiple", "Trail ATR multiple", "Best price since entry ∓ ATR × multiple", !f.trailing_enabled)}
            {toggle("opposite_signal_exit", "Exit on opposite signal")}
            {toggle("reverse_on_opposite_signal", "Reverse on opposite signal")}
          </Grid>
        </Grid>
        {problems.length > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }} data-testid="rules-problems">
            {problems.join(" · ")}
          </Alert>
        )}
        {err && (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="rules-error" onClose={() => setErr(null)}>
            {err}
          </Alert>
        )}
        {ok && (
          <Alert severity="success" sx={{ mt: 2 }} data-testid="rules-success" onClose={() => setOk(null)}>
            {ok}
          </Alert>
        )}
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          <Button variant="outlined" onClick={() => setF(PROPOSED_RULES)}>
            Apply proposed preset
          </Button>
          <Button variant="outlined" disabled={problems.length > 0 || !whatIfReady} onClick={() => onWhatIf(f)}
            title={whatIfReady ? undefined : "Pick From/To dates in the Backtest card first"}>
            Backtest these rules (not saved)
          </Button>
          <Button variant="contained" disabled={problems.length > 0 || saving} onClick={() => void save()}>
            {saving ? "Saving…" : "Save rules"}
          </Button>
          <Button disabled={saving} onClick={() => setF(readRules(effective))}>
            Reset
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
